<template>
	<SessionStart>
		<template v-slot:actionPanel>
			<div class="signup__action-panel-mail">
				<header class="signup__action-panel-header">
					<span v-if="showTrialTitle">{{ $t( "slice-apple.components.SignUp.tryOpreTitle" ) }}</span>
					<span v-else>{{ $t( "slice-apple.components.SignUp.signUpTitle" ) }}</span>
				</header>
				<div class="signup__action-panel-button-row">
					<SocialButtons
						:google-button-text="buttonText.google"
						:microsoft-button-text="buttonText.microsoft"
						:email-button-text="buttonText.email"
						:show-email-button="config.enableEmailSignup"
						@googleClick="onGoogleSignUp"
						@microsoftClick="onMicrosoftSignUp"
						@emailClick="onEmailSignUp"
					/>
				</div>
				<div class="signup__action-panel-sign-in-row">
					{{ $t( "slice-apple.components.SignUp.alreadyHaveAccountText" ) }}&nbsp;<a @click="onSignIn">{{ $t( "slice-apple.components.SignUp.signInText" ) }}</a>
				</div>
			</div>
			<footer
				class="signup__action-panel-footer-row"
				v-html="$t( 'slice-apple.components.SignUp.footer', { termsOfServiceLink, privacyPolicyLink } )"
			></footer>
		</template>

		<template v-slot:infoPanel>
			<header class="signup__info-panel-header">
				{{ $t( "slice-apple.components.SignUp.infoTitle" ) }}
			</header>
			<div class="text-ms2 signup__info-item-row">
				<div
					v-for="item in infoItems"
					v-bind:key="item.key"
					class="signup__info-item"
				>
					<div>
						<FontAwesomeIcon
							:icon="item.icon"
							class="signup__info-item-icon"
						/>
					</div>
					<div>{{ item.text }}</div>
				</div>
			</div>
			<footer class="text-ms-1 signup__info-panel-footer">
				{{ $t( "slice-apple.components.SignUp.infoFooter" ) }}
			</footer>
		</template>
	</SessionStart>
</template>

<script>
import { Source } from "@teaming/common-enums";
import { foundation } from "@teaming/slice-engine";
import SessionStart from "./SessionStart";
import SocialButtons from "./SocialButtons";

export default {
	name: "SignUp",
	inject: [ "config", ],
	components: {
		SessionStart,
		SocialButtons,
	},
	computed: {
		buttonText() {
			return {
				google: this.$t( "slice-apple.components.SignUp.googleSignUpButton" ),
				microsoft: this.$t( "slice-apple.components.SignUp.microsoftSignUpButton" ),
				email: this.$t( "slice-apple.components.SignUp.emailSignUpButton" ),
			};
		},
		showTrialTitle() {
			// the goal here is to hide the 14-day free trial title
			// if the user has come to the page from an invitation
			return !window.location.search
				.startsWith( "?redirect=/t2/invitee/welcome" );
		},
		termsOfServiceLink() {
			return foundation().config.termsLink;
		},
		privacyPolicyLink() {
			return foundation().config.policyLink;
		},
		infoItems() {
			return [
				{ key: 1, icon: [ "fad", "people-arrows", ], text: this.$t( "slice-apple.components.SignUp.infoItem1" ), },
				{ key: 2, icon: [ "fad", "bullseye-arrow", ], text: this.$t( "slice-apple.components.SignUp.infoItem2" ), },
				{ key: 3, icon: [ "fad", "calendar-check", ], text: this.$t( "slice-apple.components.SignUp.infoItem3" ), },
				{ key: 4, icon: [ "fad", "head-side-brain", ], text: this.$t( "slice-apple.components.SignUp.infoItem4" ), },
				{ key: 5, icon: [ "fad", "heartbeat", ], text: this.$t( "slice-apple.components.SignUp.infoItem5" ), },
			];
		},
	},
	methods: {
		onGoogleSignUp() {
			foundation().auth.signup( Source.GOOGLE, { target: "/", } );
		},
		onMicrosoftSignUp() {
			foundation().auth.signup( Source.OFFICE365, { target: "/", } );
		},
		onEmailSignUp() {
			foundation().auth.signup( Source.EMAIL, { target: "/", } );
		},
		onSignIn( e ) {
			e.stopPropagation();
			foundation().navigation.navigateToSignIn();
		},
	},
};
</script>

<style lang="scss" scoped>
.signup__action-panel-header {
	color: $signup-title-font-color;
	font-weight: 700;
	text-align: center;
	margin-bottom: 2.5rem;

	@include for-size('xs') {
		@include font-and-line-for(3);
	}

	@include for-size('sm') {
		@include font-and-line-for(3);
	}

	@include for-size('md') {
		@include font-and-line-for(3);
	}

	@include for-size('lg') {
		@include font-and-line-for(4);
	}
}

.signup__action-panel-mail {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	text-align: center;

	@include for-size('xs') {
		margin-top: 1.5rem;
	}

	@include for-size('sm') {
		margin-top: 2.5rem;
	}

	@include for-size('md') {
		margin-top: 2.5rem;
	}

	@include for-size('lg') {
		margin-top: 8rem;
	}
}

.signup__action-panel-sign-in-row {
	margin-top: 3rem;
	a {
		color: $default-hyperlink-font-color;
	}

	@include for-size('xs') {
		@include font-and-line-for(-1);
	}

	@include for-size('sm') {
		@include font-and-line-for(0);
	}

	@include for-size('md') {
		@include font-and-line-for(0);
	}

	@include for-size('lg') {
		@include font-and-line-for(0);
	}
}

.signup__action-panel-footer-row {
	a {
		color: $default-hyperlink-font-color;
	}

	@include for-size('xs') {
		margin-top: 2.5rem;
		@include font-and-line-for(-2);
	}

	@include for-size('sm') {
		margin-top: 2.5rem;
		@include font-and-line-for(-1);
	}

	@include for-size('md') {
		@include font-and-line-for(-1);
	}

	@include for-size('lg') {
		@include font-and-line-for(-1);
	}
}

.signup__info-panel-header {
	font-weight: 700;
	margin-bottom: 2.625rem;

	@include for-size('xs') {
		@include font-and-line-for(3);
	}

	@include for-size('sm') {
		@include font-and-line-for(3);
	}

	@include for-size('md') {
		@include font-and-line-for(5);
	}

	@include for-size('lg') {
		@include font-and-line-for(6);
	}
}

.signup__info-item-row {
	font-weight: 600;
	flex-grow: 1;
}

.signup__info-item {
	display: flex;
	text-align: left;
	margin-bottom: 1.75rem;

	div {
		:first-of-type {
			width: 1.875rem;
			margin-right: .75rem;
		}
	}
}

.signup__info-item-icon {
	--fa-primary-color: #{$signup-infoItem-icon-color-primary};
	--fa-secondary-color: #{$signup-infoItem-icon-color-secondary};
	--fa-secondary-opacity: 1;
}

.signup__info-panel-footer {
	@include for-size('xs') {
		margin-top: 1rem;
	}

	@include for-size('sm') {
		margin-top: 1rem;
	}
}
</style>
